import React, { FC, useCallback, useState } from 'react';
import Svg from 'svg-inline-react';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { MultiValue } from 'react-select';
import FrSearchSvg from '@src/svgs/fr-search.svg';
import { useGetAutoCompleteUsersSearchPatternNoPaginationLazyQuery } from '@src/graphql/generated';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { SearchPattern } from '@src/containers/Source/interfaces/searchPattern.interface';
import { useRecentSearches } from '@src/containers/Source/hooks/useRecentSearches';
import { highLightText } from '@src/containers/Source/utils/highlightText';
import { RecentSearches } from '@src/containers/Source/RecentSearches';
import { Option } from '@src/components/Select/interfaces/option.interface';
import { OutsideClick } from '@src/components/OutsideClick';
import { trackEvent } from '@src/metrics';
import { SearchField } from '../SearchFIeld';
import { KeywordSearchProps } from './props';
import './styles.scss';

export const KeywordSearch: FC<KeywordSearchProps> = ({ onSelect, values, queryParams, onQueryParamsSelect }) => {
  const [searchValue, setSearchValue] = useState('');

  const [isOpen, setIsOpen] = useState(false);

  const [getAutoCompleteUsersSearchPattern, { data }] = useGetAutoCompleteUsersSearchPatternNoPaginationLazyQuery();

  const { recentSearches, setRecentSearches, removeRecentSearch } = useRecentSearches();

  const autoCompleteOptions: SearchPattern[] = (data?.autoCompleteUsersSearchPattern.result || []).map(item => {
    return {
      id: item,
      pattern: item,
    };
  });

  const options: SearchPattern[] = [
    { id: `searchValue-${searchValue}`, pattern: searchValue?.trim() },
    ...autoCompleteOptions,
  ];

  const handleSelect = (newOptions: MultiValue<Option>) => {
    const selectedSearchPatterns = options.filter(option => newOptions.some(({ value }) => value === option.id));

    setRecentSearches({
      ...queryParams,
      searchPatterns: selectedSearchPatterns,
    });

    onSelect(selectedSearchPatterns);

    trackEvent({
      context: MetricContext.SOURCING_ENTRY_PAGE,
      interaction: MetricAction.SELECT_SEARCH_PATTERNS,
      payload: {
        'Filter Type': 'Keyword',
        'Filter Value': selectedSearchPatterns.map(({ pattern }) => pattern).join(', '),
      },
    });
  };

  const onOptionClick = (option: SearchPattern) => {
    onSelect([...values, option]);

    setSearchValue('');

    setIsOpen(false);
  };

  const onGetAutoCompleteUsersSearchPattern = useCallback(
    debounce((searchPattern: string) => {
      getAutoCompleteUsersSearchPattern({
        variables: {
          paginationOptions: {
            limit: 5,
          },
          searchPattern: searchPattern,
        },
      });
    }, 350),
    [],
  );

  const onSearch = (searchValue: string) => {
    const doesSearchValueExist = !!searchValue?.trim().length;

    if (doesSearchValueExist) {
      onGetAutoCompleteUsersSearchPattern(searchValue);
    }

    setSearchValue(searchValue);

    setIsOpen(doesSearchValueExist);
  };

  const onBlur = () => {
    if (searchValue.length) {
      onOptionClick(options[0]);
    }
  };

  return (
    <OutsideClick
      addOutsideClickListener={isOpen}
      onOutsideClick={() => {
        onBlur();

        setIsOpen(false);
      }}
    >
      <div className="start-headhunt-keyword-search">
        <Svg
          className={classnames(
            'start-headhunt-keyword-search__icon',
            isOpen && 'start-headhunt-keyword-search__icon--active',
          )}
          src={FrSearchSvg}
        />

        <SearchField
          searchValue={searchValue}
          setIsOpen={setIsOpen}
          onSearch={onSearch}
          options={options}
          onOptionClick={onOptionClick}
          handleSelect={handleSelect}
          values={values}
        />

        {isOpen && (!!searchValue.length || !!recentSearches?.length) && (
          <div className="start-headhunt-keyword-search__dropdown-wrapper">
            <div className="start-headhunt-keyword-search__autocomplete-wrapper">
              <div className="start-headhunt-keyword-search__section-title">Suggestions</div>

              {options.map(
                option =>
                  !!option.pattern && (
                    <div
                      className={classnames(
                        'start-headhunt-keyword-search__option',
                        option.id === 'searchValue' && 'start-headhunt-keyword-search__option--bold',
                      )}
                      key={option.id}
                      onClick={() => onOptionClick(option)}
                    >
                      {highLightText(searchValue, option.pattern)}
                    </div>
                  ),
              )}
            </div>

            <div className="start-headhunt-keyword-search__recent-wrapper">
              <div className="start-headhunt-keyword-search__section-title">Recent Searches</div>

              <RecentSearches
                onQueryParamsSelect={onQueryParamsSelect}
                recentSearches={recentSearches || []}
                removeRecentSearch={removeRecentSearch}
              />
            </div>
          </div>
        )}
      </div>
    </OutsideClick>
  );
};
